<template>
    <modal-side-generic
	sizeClass="big"
    :title="t('cards.batchfile.newCharge.reuseModal.title')"
    :subTitle="t('cards.batchfile.newCharge.reuseModal.subTitle')"
	:triggerClose="triggerClose"
	@hideMe="[emit('hideMe'), triggerClose = false]">
        <!-- Loader -->
        <loader-generic v-if="showLoader" fullSize/>
        <!-- Table -->
        <table-generic
        v-if="!showLoader && !applyErrorState  && !!getLastBatchfileList.length"
        :headerData="headerData"
        :tableData="tableData"
        :tableStyles="tableStyles"
        @sortBy="tableSortByHandler"
        @rowClicked="rowClickHandler"/>
        <!-- Empty State -->
        <empty-state-generic
        v-if="!showLoader && !applyErrorState && !getLastBatchfileList.length"
        style="height: 100%;"
        :illustration="emptyStateIllustration"
        :title="emptyStateTitle"
        :description="t('cards.batchfile.newCharge.reuseModal.emptyStateDescription')"/>
        <!-- Error State -->
        <empty-state-error v-if="applyErrorState" :message="t('genericErrorMessage')"/>
        <!-- Feedback Toast -->
        <toast-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>
    </modal-side-generic>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue & Assets */
import { ref, computed } from 'vue'
import { sortTableData } from '@/assets/scripts/parsers/tables'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'reuseCharge'])

const props = defineProps({
    currentCardGroupInfo: Object
})

/* mapActions & mapGetters */
const { requestLastBatchfileChargeList } = mapActions(moduleMapper.CARDS_BATCHFILE_NEW_CHARGE)
const { getLastBatchfileList } = mapGetters(moduleMapper.CARDS_BATCHFILE_NEW_CHARGE)

/* Data */
const triggerClose = ref(false)
const showLoader = ref(false)
const applyErrorState = ref(false)

const emptyStateIllustration = computed(() => {
    switch (props.currentCardGroupInfo.productIds[0]) {
        case 'MCX_PPNP_':
            return 'emptyState/cards-multicaixa'
        case 'N_PER_SNL':
        case 'C_FLEX_EMV':
        case 'PER_SONANG':
            return 'emptyState/cards-flex'
        default:
            return 'emptyState/cards-kamba'
    }
})

const emptyStateTitle = computed(() => {
    switch (props.currentCardGroupInfo.productIds[0]) {
        case 'MCX_PPNP_':
            return t('cards.batchfile.newCharge.reuseModal.emptyStateTitleMulticaixa')
        case 'N_PER_SNL':
        case 'C_FLEX_EMV':
        case 'PER_SONANG':
            return t('cards.batchfile.newCharge.reuseModal.emptyStateTitleFlex')
        default:
            return t('cards.batchfile.newCharge.reuseModal.emptyStateTitleKamba')
    }
})

/*************************************************
*                                                *
*                    Table Data                  *
*                                                *
*************************************************/
const tableData = ref(null)
const headerData = ref([
    { label: t('cards.batchfile.newCharge.reuseModal.description'), dataAttr: 'sourceReference', rowCellType: 'title', sortable: true },
    { label: t('cards.batchfile.newCharge.reuseModal.totalAmount'), dataAttr: 'totalAmount', sortable: true },
    { label: t('cards.batchfile.newCharge.reuseModal.totalEntries'), dataAttr: 'totalEntries', rowCellType: 'amount', sortable: true }
])

const tableStyles = [null, { 'text-align': 'end' }, { width: '120px', 'min-width': '120px' }]

const rowClickHandler = $event => {
	emit('reuseCharge', getLastBatchfileList.value.find(entry => entry.id === $event.rowId))
    triggerClose.value = true
}

const tableSortByHandler = $event => {
    tableData.value = sortTableData(tableData.value, $event)
}
/*************************************************
*                                                *
*                 Initial Request                *
*                                                *
*************************************************/
const initialRequests = async () => {
    showLoader.value = true

    const payload = {
        pageSize: 10,
        cardGroupId: props.currentCardGroupInfo.id
    }

    try {
        await requestLastBatchfileChargeList(payload)
        tableData.value = getLastBatchfileList.value.map(entry => {
            return [
                { value: entry.id, dataAttr: 'id' },
                { value: entry.sourceReference || '-', dataAttr: 'sourceReference' },
                {
                    type: 'amount',
                    value: { content: entry.totalAmount || '-', currency: entry.totalAmount ? entry.currency : null, noBalanceIcon: true },
                    dataAttr: 'totalAmount'
                },
                { value: entry.totalEntries || '-', dataAttr: 'totalEntries' }
            ]
        })
        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response.data, initialRequests)
    }
}

/*************************************************
*                                                *
*                  Error Handler                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequests()
</script>

<style lang="scss" scoped>
</style>
